import { useState, useEffect, useCallback } from 'react';
import { BACKEND, MEDIA_STUDIO_API_KEY, SCHEDULER_BACKEND, C, GREEN, F } from '../constants';
import { getUserId, computeWeeklyDates } from '../utils';

// Sensible default tags so this field is never blank going in — brand tags
// that apply to every video here, plus "Shorts" since everything in this
// queue is a YouTube Short. Still fully editable per-video before posting.
const DEFAULT_TAGS = 'StrategicHonesty,Leadership,Integrity,Shorts';

// Self-contained "awaiting YouTube" review queue. Deliberately independent of
// ReviewPublish's existing (large, buffer-queue-focused) state machine — this
// reads/writes Media Studio's asset_store directly and schedules real posts
// through the scheduler backend, with no shared state to accidentally break.
export default function YoutubeQueue({ onCountChange }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({}); // id -> { title, description, tags, scheduledAt }
  const [posting, setPosting] = useState(false);
  const [results, setResults] = useState({}); // id -> { state: 'ok'|'err', msg }
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (onCountChange) onCountChange(assets.length);
  }, [assets.length, onCountChange]);

  const fetchAwaiting = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/assets?type=video`, { headers: { 'x-api-key': MEDIA_STUDIO_API_KEY } });
      const data = await res.json();
      const awaiting = (Array.isArray(data) ? data : []).filter(a => a.approved_for_youtube && !a.youtube_scheduled);
      setAssets(awaiting);
      setEdits(prev => {
        const dates = computeWeeklyDates(awaiting.length);
        const next = { ...prev };
        awaiting.forEach((a, i) => {
          if (!next[a.id]) {
            next[a.id] = {
              title: (a.title || '').slice(0, 100),
              description: a.description || '',
              tags: a.tags || DEFAULT_TAGS,
              scheduledAt: dates[i].toISOString().slice(0, 16),
            };
          }
        });
        return next;
      });
    } catch {
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAwaiting(); }, [fetchAwaiting]);

  const updateEdit = (id, field, value) => setEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  async function postAll() {
    setPosting(true);
    const userId = localStorage.getItem('sh_youtube_userId') || getUserId();
    const nextResults = {};
    for (const asset of assets) {
      const edit = edits[asset.id];
      if (!edit?.title?.trim()) { nextResults[asset.id] = { state: 'err', msg: 'Title required' }; continue; }

      // Claim first (atomic conditional update) — guards against the
      // backend's weekly auto-scheduler claiming the same video at nearly
      // the same moment as a manual click here.
      let claimed = false;
      try {
        const claimData = await (await fetch(`${BACKEND}/api/assets/${asset.id}?ifYoutubeScheduled=false`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'x-api-key': MEDIA_STUDIO_API_KEY },
          body: JSON.stringify({ youtube_scheduled: true }),
        })).json();
        claimed = claimData.changed === true;
      } catch (e) {
        nextResults[asset.id] = { state: 'err', msg: `Claim failed: ${e.message}` };
        continue;
      }
      if (!claimed) {
        nextResults[asset.id] = { state: 'err', msg: 'Already handled elsewhere (auto-scheduler or another tab)' };
        continue;
      }

      try {
        const data = await (await fetch(`${SCHEDULER_BACKEND}/posts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            platform: 'youtube',
            userId,
            content: edit.description,
            mediaUrl: asset.url,
            mediaType: 'video',
            title: edit.title,
            tags: edit.tags || null,
            scheduledAt: new Date(edit.scheduledAt).toISOString(),
          }),
        })).json();
        if (!data.success) throw new Error(data.error || 'Schedule failed');

        // Tracking-only entry so this shows up on the Calendar tab too — the
        // real publish is entirely driven by the /posts row above; this just
        // makes it visible where the user already looks for what's upcoming.
        fetch(`${BACKEND}/api/scheduled-calendar`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-api-key': MEDIA_STUDIO_API_KEY },
          body: JSON.stringify({
            platform: 'youtube shorts',
            content: edit.title,
            mediaUrl: asset.url,
            scheduledAt: new Date(edit.scheduledAt).toISOString(),
            ideaId: asset.idea_id || null,
          }),
        }).catch(() => {});

        nextResults[asset.id] = { state: 'ok', msg: `Scheduled for ${new Date(edit.scheduledAt).toLocaleDateString()}` };
      } catch (e) {
        // Release the claim so this asset gets retried later (manually or
        // by the weekly auto-scheduler) instead of being stuck "claimed"
        // forever with nothing actually scheduled.
        fetch(`${BACKEND}/api/assets/${asset.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'x-api-key': MEDIA_STUDIO_API_KEY },
          body: JSON.stringify({ youtube_scheduled: false }),
        }).catch(() => {});
        nextResults[asset.id] = { state: 'err', msg: e.message };
      }
    }
    setResults(nextResults);
    setPosting(false);
    fetchAwaiting();
  }

  const labelStyle = { fontSize: 11, color: C.muted, marginBottom: 3, display: 'block', fontWeight: 500 };
  const inputStyle = { width: '100%', border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 8px', fontSize: 12, color: C.text, fontFamily: F, outline: 'none', background: '#fff', boxSizing: 'border-box', marginBottom: 8 };

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 16 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>▶️</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>YouTube — Awaiting Post</span>
          <span style={{ fontSize: 11, color: '#fff', background: '#FF0000', borderRadius: 10, padding: '1px 8px', fontWeight: 600 }}>{assets.length}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {assets.length > 0 && (
            <button
              onClick={e => { e.stopPropagation(); postAll(); }}
              disabled={posting}
              style={{ padding: '6px 14px', background: posting ? '#e0e0e0' : GREEN, color: posting ? '#999' : '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: posting ? 'not-allowed' : 'pointer' }}
            >
              {posting ? 'Scheduling…' : `🚀 Post All (${assets.length})`}
            </button>
          )}
          <span style={{ fontSize: 12, color: C.muted }}>{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          {loading ? (
            <div style={{ fontSize: 12, color: C.muted }}>Loading…</div>
          ) : assets.length === 0 ? (
            <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic' }}>Nothing awaiting — approve a video for YouTube in Review & Publish or the Media Store.</div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                {assets.map(asset => {
                  const edit = edits[asset.id] || {};
                  const result = results[asset.id];
                  return (
                    <div key={asset.id} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, background: '#fafafa' }}>
                      <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, wordBreak: 'break-all' }}>🎬 {asset.url}</div>
                      <label style={labelStyle}>Title</label>
                      <input value={edit.title || ''} onChange={e => updateEdit(asset.id, 'title', e.target.value.slice(0, 100))} style={inputStyle} />
                      <label style={labelStyle}>Description</label>
                      <textarea value={edit.description || ''} onChange={e => updateEdit(asset.id, 'description', e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                      <label style={labelStyle}>Tags</label>
                      <input value={edit.tags || ''} onChange={e => updateEdit(asset.id, 'tags', e.target.value)} style={inputStyle} placeholder="comma-separated" />
                      <label style={labelStyle}>Scheduled date</label>
                      <input type="datetime-local" value={edit.scheduledAt || ''} onChange={e => updateEdit(asset.id, 'scheduledAt', e.target.value)} style={{ ...inputStyle, marginBottom: 0 }} />
                      {result && (
                        <div style={{ marginTop: 6, fontSize: 12, color: result.state === 'ok' ? '#16a34a' : '#dc2626' }}>
                          {result.state === 'ok' ? '✓' : '✗'} {result.msg}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
